import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';

const ModelEngine = () => {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Initialize variables
    let camera, controls, scene, renderer, model;
    let animationFrameId;

    // Initialize Three.js scene
    const init = () => {
      // Scene setup
      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x000000);
      
      // Camera setup - matching the original
      camera = new THREE.PerspectiveCamera(30, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.set(0, 10, 25); // x=0, y=10 (up), z=25 (back)
      
      // Renderer setup
      renderer = new THREE.WebGLRenderer({ 
        antialias: true,
        powerPreference: "high-performance"
      });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      
      // Clear previous canvas if exists
      if (mountRef.current.children.length > 0) {
        mountRef.current.removeChild(mountRef.current.children[0]);
      }
      mountRef.current.appendChild(renderer.domElement);
      
      // Controls setup - matching original constraints
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableZoom = true;
      controls.minDistance = 315;
      controls.maxDistance = 335;
      controls.target.set(0, -2, 0); // Looking slightly downward
      controls.update();
      
      // Lighting - matching original
      const ambientLight = new THREE.AmbientLight(0x404040);
      scene.add(ambientLight);
      
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
      directionalLight.position.set(1, 1, 1);
      scene.add(directionalLight);
      
      // Wireframe material - matching original style
      const wireframeMat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(0x40a3ff),
        wireframe: true,
        transparent: true,
        opacity: 0.2
      });
      
      // Setup DRACO loader
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
      
      // GLTF Loader with DRACO support
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);
      
      // Load model
      loader.load(
        'https://s3.us-east-1.amazonaws.com/www.outlne.com/3D+Models/belinidraco.glb',
        (gltf) => {
          setLoading(false);
          model = gltf.scene;
          
          // Apply wireframe material to all meshes
          model.traverse((child) => {
            if (child.isMesh) {
              child.material = wireframeMat;
            }
          });
          
          // Center the model
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          model.position.sub(center);
          
          // Scale adjustment - may need tweaking for your specific model
          model.scale.set(1.5, 1.5, 1.5);
          
          scene.add(model);
          
          // Update controls target after model is loaded
          controls.target.copy(center);
          controls.target.y -= 5; // Same downward adjustment
          controls.update();
          
          // Start animation
          animate();
        },
        (xhr) => {
          // Progress updates
          const percentLoaded = (xhr.loaded / xhr.total * 100);
          setProgress(percentLoaded);
          console.log(`${percentLoaded.toFixed(0)}% loaded`);
        },
        (error) => {
          console.error('Error loading model:', error);
          setError('Error loading model. Please check console for details.');
        }
      );
    };

    // Animation loop - same rotation speed
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (model) model.rotation.y += 0.002;
      controls.update();
      renderer.render(scene, camera);
    };

    // Handle window resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    // Initialize and set up event listeners
    init();
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (mountRef.current && renderer?.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div style={{ 
      position: 'relative',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: '#000'
    }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      
      {/* Loading/error overlay */}
      {(loading || error) && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: '20px',
          borderRadius: '8px',
          minWidth: '300px'
        }}>
          {error ? (
            <>
              <h3 style={{ marginTop: 0 }}>Error Loading Model</h3>
              <p>{error}</p>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  background: '#40a3ff',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Try Again
              </button>
              <p style={{ marginTop: '15px', fontSize: '14px' }}>
                If the problem persists, check your console (F12) for details.
              </p>
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Loading 3D Model</h3>
              <div style={{
                width: '100%',
                height: '20px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '10px',
                margin: '15px 0',
                overflow: 'hidden'
              }}>
                <div 
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #40a3ff, #80c1ff)',
                    transition: 'width 0.3s ease',
                    borderRadius: '10px'
                  }}
                />
              </div>
              <p>{Math.round(progress)}% loaded</p>
              <p style={{ fontSize: '14px', marginTop: '10px' }}>
                Using Draco compression for faster loading...
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelEngine;